import { Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { Modal } from 'components-antd';
import {
  getTimePeriodDueFromSelector,
  getTimePeriodDueToSelector,
  getTimePeriodSelector,
} from 'store/selectors/clarity';
import { Wrapper as PendingWrapper } from 'components';
import { useFetchVolumeStats } from 'pages/ClarityRevamped/hooks';
import { ExportCSV } from 'pages/ClarityRevamped/Components/ExportCSV';
import { generateCSVFileName } from '../../helper';

import styles from '../styles.module.scss';
import { ModalFooter } from '../ModalFooter';
import classNames from 'classnames';
import { TransactionIcon } from '../../TransactionIcon';

interface DataType {
  Date: string;
  Transaction: string;
  TotalVolume: number;
  PropertyTransactionId: number;
  RepresentingSide: string;
  Agents: string;
  TransactionStatus: string;
  Address1: string;
  Address2: string;
  AddressUnit: string;
  RepresentingRole: string;
}

export interface SummaryType {
  NumberTransactions: number;
  TotalVolume: number;
}

export const VolumeTableModal = ({
  title,
  summaryType,
  year,
  month,
  isoWeek,
  date,
  open,
  onCancel,
  selectedAgent,
  color,
}) => {
  const dueFrom = useSelector(getTimePeriodDueFromSelector);
  const dueTo = useSelector(getTimePeriodDueToSelector);
  let timePeriod = useSelector(getTimePeriodSelector);
  const [data, setData] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [summary, setSummary] = useState<SummaryType>();

  if (timePeriod === moment().subtract(1, 'year').year()) timePeriod = 'YTD';

  const getStats = useFetchVolumeStats();

  const getDateTitle = (type) => {
    if (type === 'Listings' || type === 'Purchases') return 'Status Date';
    else if (type === 'Canceled') return 'Canceled Date';
    return 'Closing Date';
  };

  const footerStyles = {
    'background-color': `${color}14`,
    'border-radius': '4px',
  };

  const statusClasses = {
    'Under contract': 'underContract',
    Closed: 'closed',
    Expired: 'expired',
    'Coming Soon': 'comingSoon',
    'Clear to close': 'clearToClose',
    Listed: 'listed',
    Canceled: 'canceled',
    Upcoming: 'upcoming',
    'In Progress': 'inProgress',
    Completed: 'completed',
    'On Hold': 'onHold',
    'At Risk': 'atRisk',
    PreListing: 'preListing',
    OfficeExclusive: 'officeExclusive',
    ComingSoon: 'comingSoon',
    ActiveListing: 'activeListing',
    OnHold: 'onHold',
  };

  const statusLabels = {
    'Under contract': 'Under Contract',
    Closed: 'Closed',
    Expired: 'Expired',
    'Coming Soon': 'Coming Soon',
    'Clear to close': 'Clear to close',
    Listed: 'Listed',
    Canceled: 'Canceled',
    Upcoming: 'Upcoming',
    'In Progress': 'In Progress',
    Completed: 'Completed',
    'On Hold': 'On Hold',
    'At Risk': 'At Risk',
    PreListing: 'PreListing',
    OfficeExclusive: 'Office Exclusive',
    ComingSoon: 'Coming Soon',
    ActiveListing: 'Active Listing',
    OnHold: 'On Hold',
  };

  const volumeColumns: TableProps<DataType>['columns'] = [
    {
      title: 'Transaction',
      dataIndex: 'Transaction',
      key: 'Transaction',
      sorter: {
        compare: (record1, record2) => {
          const a = record1.Transaction;
          const b = record2.Transaction;
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
      render(_, record) {
        return (
          <div
            onClick={() =>
              window.open(
                `${window.location.origin}/workshop/transactions/${record.PropertyTransactionId}/overview`,
                '_blank',
              )
            }
            className={styles.transactionLink}
          >
            <div className={styles.address}>
              <p className={styles.address1}>
                {record.Address1}
                {record.AddressUnit}
              </p>
              <p className={styles.address2}>{record.Address2}</p>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'TotalVolume',
      key: 'TotalVolume',
      sorter: {
        compare: (record1, record2) => {
          const a = record1.TotalVolume;
          const b = record2.TotalVolume;
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
      render: (TotalVolume) => {
        return (
          <p className={styles.amountColumn}>
            {TotalVolume >= 0
              ? `$${TotalVolume.toLocaleString()}`
              : `-$${Math.abs(TotalVolume).toLocaleString()}`}
          </p>
        );
      },
    },
    {
      title: 'Agent',
      dataIndex: 'Agents',
      key: 'Agents',
      sorter: {
        compare: (record1, record2) => {
          const a = record1.Agents;
          const b = record2.Agents;
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
      render: (Agents, record) => {
        return (
          <div className={styles.agentColumn}>
            {Agents?.map((agent) => (
              <p className={styles.agent}>{agent}</p>
            ))}
            <p className={styles.role}>Representing: {record.RepresentingRole}</p>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'TransactionStatus',
      key: 'TransactionStatus',
      sorter: {
        compare: (record1, record2) => {
          const a = moment(record1.TransactionStatus);
          const b = moment(record2.TransactionStatus);
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
      render(value) {
        return (
          <span className={classNames(styles.status, styles[statusClasses[value]])}>
            {statusLabels[value]}
          </span>
        );
      },
    },
    {
      title: getDateTitle(summaryType),
      dataIndex: 'Date',
      key: 'Date',
      sorter: {
        compare: (record1, record2) => {
          const a = moment(record1.Date);
          const b = moment(record2.Date);
          return a > b ? -1 : b > a ? 1 : 0;
        },
      },
      render(value) {
        return <span className={styles.dateColumn}>{value?.replaceAll('-', '/')}</span>;
      },
    },
  ];

  const formatCSVData = (data) => {
    return data?.map((row) => {
      return {
        Transaction: `"${row.Transaction}"`,
        Amount:
          row.TotalVolume >= 0
            ? `"$${row.TotalVolume.toLocaleString()}"`
            : `-"$${Math.abs(row.TotalVolume).toLocaleString()}"`,
        Agent: `"${row.Agents.join(', ')}"`,
        RepresentingRole: row.RepresentingRole,
        Status: statusLabels[row.TransactionStatus],
        'Status Date': row.Date,
      };
    });
  };

  useEffect(() => {
    getStats({
      setSummary,
      setData,
      setIsPending,
      summaryType: summaryType,
      aggregateDataFormat: 'Tabular',
      year: year,
      quarter: null,
      month: timePeriod === 'YTD' ? month : null,
      isoWeek: timePeriod === 'QTD' ? isoWeek : null,
      date: timePeriod === 'MTD' || timePeriod === 'WEEK' || dueFrom || dueTo ? date : null,
      transactionAgentUserId: selectedAgent?.TransactionAgentUserId,
      transactionAgentContactId: selectedAgent?.TransactionAgentContactId,
      expand: true,
    });
  }, [timePeriod, year, month, isoWeek, dueFrom, dueTo]);

  return (
    <Modal
      width={1000}
      className={styles.clarityTableModal}
      open={open}
      footer={null}
      onCancel={onCancel}
    >
      <header className={styles.tableHeader}>
        <h2 className={styles.modalHeader}>{title}</h2>
        <ExportCSV data={formatCSVData(data)} fileName={generateCSVFileName(title)} />
      </header>
      <PendingWrapper isPending={isPending} className={styles.tableContainer}>
        <Table
          className={styles.statsTable}
          columns={volumeColumns}
          dataSource={data}
          pagination={false}
        />
      </PendingWrapper>
      <ModalFooter amount={`$${summary?.TotalVolume.toLocaleString() || 0}`} color={color} />
    </Modal>
  );
};
